import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, CheckCircle2, Lock } from 'lucide-react';
import { formatCurrency } from '../lib/utils';
export interface Scenario {
  intent: 'purchase' | 'refinance' | 'equity';
  budget?: number;
  downPayment?: number;
  homeValue?: number;
  currentBalance?: number;
  currentRate?: number;
  loanTerm?: number;
  monthlyIncome?: number;
  monthlyDebts?: number;
  timeline?: string;
  agent?: string;
  goal?: string;
  cashOutAmount?: number;
  propertyState?: string;
  targetRate?: number;
  underContract?: string;
  name?: string;
  email?: string;
  phone?: string;
  zip?: string;
}
interface ScenarioSummaryProps {
  scenario: Scenario;
  estimatedPayment?: number;
  currentPayment?: number;
  monthlySavings?: number;
  accessibleEquity?: number;
  progress: number;
}
export function ScenarioSummary({
  scenario,
  estimatedPayment,
  currentPayment,
  monthlySavings,
  accessibleEquity,
  progress
}: ScenarioSummaryProps) {
  const isEquity = scenario.intent === 'equity';
  const items: {
    label: string;
    value: string;
  }[] = [];
  // Rates first — these are the headline variables now that the calculator
  // is gone and rates are the first thing the borrower enters in chat.
  if (scenario.currentRate) items.push({
    label: 'Current rate',
    value: `${scenario.currentRate}%`
  });
  if (scenario.targetRate) items.push({
    label: 'Target rate',
    value: `${scenario.targetRate}%`
  });
  if (scenario.intent === 'purchase') {
    if (scenario.budget) items.push({
      label: 'Target price',
      value: formatCurrency(scenario.budget)
    });
    if (scenario.downPayment != null) items.push({
      label: 'Down payment',
      value: formatCurrency(scenario.downPayment)
    });
    if (scenario.loanTerm) items.push({
      label: 'Loan term',
      value: `${scenario.loanTerm} years`
    });
  } else if (scenario.intent === 'refinance') {
    if (scenario.homeValue) items.push({
      label: 'Home value',
      value: formatCurrency(scenario.homeValue)
    });
    if (scenario.currentBalance) items.push({
      label: 'Loan balance',
      value: formatCurrency(scenario.currentBalance)
    });
    if (scenario.loanTerm) items.push({
      label: 'New loan term',
      value: `${scenario.loanTerm} years`
    });
    if (scenario.goal) items.push({
      label: 'Goal',
      value: scenario.goal
    });
  } else {
    if (scenario.homeValue) items.push({
      label: 'Home value',
      value: formatCurrency(scenario.homeValue)
    });
    if (scenario.currentBalance) items.push({
      label: 'Mortgage balance',
      value: formatCurrency(scenario.currentBalance)
    });
    if (scenario.cashOutAmount) items.push({
      label: 'Cash needed',
      value: formatCurrency(scenario.cashOutAmount)
    });
    if (scenario.goal) items.push({
      label: 'Using it for',
      value: scenario.goal
    });
  }
  if (scenario.propertyState) items.push({
    label: 'Property state',
    value: scenario.propertyState
  });
  if (scenario.underContract) items.push({
    label: 'Under contract',
    value: scenario.underContract
  });
  if (scenario.monthlyDebts != null) items.push({
    label: 'Monthly debts',
    value: formatCurrency(scenario.monthlyDebts)
  });
  if (scenario.timeline) items.push({
    label: 'Timeline',
    value: scenario.timeline
  });
  if (scenario.agent) items.push({
    label: 'Has agent',
    value: scenario.agent
  });
  if (scenario.name) items.push({
    label: 'Name',
    value: scenario.name
  });
  if (scenario.email) items.push({
    label: 'Email',
    value: scenario.email
  });
  const intentLabel = scenario.intent === 'purchase' ? 'buying a home' : scenario.intent === 'refinance' ? 'refinancing' : 'home equity';
  return <div className="bg-forest-600 rounded-3xl p-6 sm:p-7 shadow-xl text-white relative overflow-hidden h-full flex flex-col" data-id="element-189">
      <div className="absolute top-0 right-0 w-64 h-64 bg-lime/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-190" />

      <div className="relative z-10 flex-1" data-id="element-191">
        <div className="flex items-center gap-2 mb-1" data-id="element-192">
          <Sparkles className="w-4 h-4 text-lime-300" data-id="element-193" />
          <span className="text-xs uppercase tracking-wider font-bold text-lime-300" data-id="element-194">
            Your scenario
          </span>
        </div>
        <h3 className="text-xl font-extrabold mb-5 lowercase" data-id="element-195">{intentLabel}</h3>

        {/* Progress bar */}
        <div className="mb-6" data-id="element-196">
          <div className="flex justify-between items-center mb-2 text-xs" data-id="element-197">
            <span className="text-white/70" data-id="element-198">building your profile</span>
            <span className="text-white font-bold" data-id="element-199">
              {Math.round(progress * 100)}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-forest-700 rounded-full overflow-hidden" data-id="element-200">
            <motion.div className="h-full bg-gradient-to-r from-lime to-lime-300" initial={{
            width: 0
          }} animate={{
            width: `${progress * 100}%`
          }} transition={{
            type: 'spring',
            damping: 20
          }} data-id="element-201" />
          </div>
        </div>

        {/* Collected items */}
        {items.length === 0 ? <div className="text-center py-12 text-white/60 text-sm" data-id="element-202">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest-700 mb-3" data-id="element-203">
              <Sparkles className="w-5 h-5 text-lime-300" data-id="element-204" />
            </div>
            <p data-id="element-205">Pronto is gathering your details.</p>
            <p className="text-xs mt-1 text-white/50" data-id="element-206">
              Answer the questions and your scenario will build here.
            </p>
          </div> : <div className="space-y-2" data-id="element-207">
            <AnimatePresence initial={false} data-id="element-208">
              {items.map(item => <motion.div key={item.label} initial={{
            opacity: 0,
            y: 8,
            height: 0
          }} animate={{
            opacity: 1,
            y: 0,
            height: 'auto'
          }} transition={{
            type: 'spring',
            damping: 22
          }} className="flex items-center justify-between py-2 px-3 bg-forest-700/60 border border-forest-500/40 rounded-xl" data-id="element-209">
                  <div className="flex items-center gap-2" data-id="element-210">
                    <CheckCircle2 className="w-4 h-4 text-lime-300 shrink-0" data-id="element-211" />
                    <span className="text-sm text-white/80" data-id="element-212">{item.label}</span>
                  </div>
                  <span className="text-sm font-bold text-white" data-id="element-213">
                    {item.value}
                  </span>
                </motion.div>)}
            </AnimatePresence>
          </div>}

        {/* Accessible Home Equity (HELOC headline) */}
        <AnimatePresence data-id="element-214">
          {isEquity && accessibleEquity != null && accessibleEquity > 0 && <motion.div initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 22
        }} className="mt-6 p-5 bg-lime/15 border border-lime/30 rounded-2xl" data-id="element-215">
              <p className="text-xs text-lime-200 font-bold uppercase tracking-wider mb-1" data-id="element-216">
                Accessible home equity
              </p>
              <div className="flex items-baseline gap-1" data-id="element-217">
                <motion.span key={accessibleEquity} initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-3xl font-extrabold text-white tracking-tight" data-id="element-218">
                  {formatCurrency(accessibleEquity)}
                </motion.span>
              </div>
              <p className="text-xs text-white/50 mt-2" data-id="element-219">
                Based on 90% of your home value, minus your current mortgage.
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Estimated Payment Reveal (purchase, refinance, HELOC) */}
        <AnimatePresence data-id="element-220">
          {estimatedPayment != null && estimatedPayment > 0 && <motion.div initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 22,
          delay: isEquity ? 0.1 : 0
        }} className={`p-5 rounded-2xl border ${isEquity ? 'mt-3 bg-white/5 border-white/10' : 'mt-6 bg-lime/15 border-lime/30'}`} data-id="element-221">
              <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isEquity ? 'text-white/70' : 'text-lime-200'}`} data-id="element-222">
                {isEquity ? 'Approx equity payment' : currentPayment != null && currentPayment > 0 ? 'New estimated payment' : 'Estimated monthly payment'}
              </p>
              <div className="flex items-baseline gap-1" data-id="element-223">
                <motion.span key={estimatedPayment} initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-3xl font-extrabold text-white tracking-tight" data-id="element-224">
                  {formatCurrency(estimatedPayment)}
                </motion.span>
                <span className="text-sm text-white/60" data-id="element-225">/mo</span>
              </div>

              {!isEquity && currentPayment != null && currentPayment > 0 && <div className="mt-3 pt-3 border-t border-lime/20 flex items-baseline justify-between" data-id="element-226">
                  <span className="text-xs text-white/60" data-id="element-227">Current payment</span>
                  <span className="text-sm font-bold text-white/80" data-id="element-228">
                    {formatCurrency(currentPayment)}
                    <span className="text-white/40 font-normal" data-id="element-229">/mo</span>
                  </span>
                </div>}

              <p className="text-xs text-white/50 mt-2" data-id="element-230">
                {isEquity ? 'Interest-only estimate at 7% APR on the cash you pull out.' : 'Principal & interest only. Taxes & insurance vary by area.'}
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Estimated Monthly Savings (refinance only) */}
        <AnimatePresence data-id="element-231">
          {monthlySavings != null && monthlySavings !== 0 && <motion.div initial={{
          opacity: 0,
          y: 12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 22,
          delay: 0.1
        }} className={`mt-3 p-5 rounded-2xl border ${monthlySavings > 0 ? 'bg-coral/15 border-coral/40' : 'bg-white/5 border-white/10'}`} data-id="element-232">
              <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${monthlySavings > 0 ? 'text-coral-200' : 'text-white/60'}`} data-id="element-233">
                {monthlySavings > 0 ? 'Estimated monthly savings' : 'Monthly difference'}
              </p>
              <div className="flex items-baseline gap-1" data-id="element-234">
                <motion.span key={monthlySavings} initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-3xl font-extrabold text-white tracking-tight" data-id="element-235">
                  {formatCurrency(Math.abs(monthlySavings))}
                </motion.span>
                <span className="text-sm text-white/60" data-id="element-236">/mo</span>
              </div>
              <p className="text-xs text-white/60 mt-2" data-id="element-237">
                {monthlySavings > 0 ? `That's about ${formatCurrency(monthlySavings * 12)} a year you could keep in your pocket.` : 'At this rate, your new payment would be slightly higher. A Loan Pronto advisor can help you find a better option.'}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="relative z-10 mt-6 pt-5 border-t border-forest-500/40 flex items-center gap-2 text-xs text-white/50" data-id="element-238">
        <Lock className="w-3 h-3" data-id="element-239" />
        Your info stays private. No credit check required.
      </div>
    </div>;
}